import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton } from 'primereact/selectbutton';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';

import FormTributos from '../components/FormTributos';
import { validarRegrasTributarias, resumoErros } from '../regrasTributarias';
import {
  pesquisarRecebimentos, reabrirRecebimento, conferirLoteRecebimentos, resumoNcm, mensagemErro,
} from '../tributosApi';
import {
  StatusTag, formatarCnpj, formatarNcm, dateToIso, normalizarTributos,
} from '../util';

const soDigitos = v => String(v || '').replace(/\D/g, '');
/** Valor comum a todos os itens (ou '' quando diferem). */
const valorComum = (itens, campo) => {
  const valores = [...new Set(itens.map(i => soDigitos(i[campo])))];
  return valores.length === 1 ? valores[0] : '';
};

const OPCOES_STATUS = [
  { label: 'Pendentes', value: 'PENDENTE' },
  { label: 'Conferidos', value: 'CONFERIDO' },
  { label: 'Já atualizados', value: 'ATUALIZADO' },
  { label: 'Ignorados', value: 'IGNORADO' },
  { label: 'Todos', value: 'TODOS' },
];

const INTERVALO_MS = 30000;

export default function Recebimentos({ onAlteracao }) {
  const navigate = useNavigate();

  const [status, setStatus] = useState('PENDENTE');
  const [codigoEan, setCodigoEan] = useState('');
  const [descricao, setDescricao] = useState('');
  const [periodo, setPeriodo] = useState(() => {
    const ini = new Date();
    ini.setDate(ini.getDate() - 7);
    return [ini, new Date()];
  });
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const idsConhecidos = useRef(null);

  // ---- Seleção múltipla / conferência em lote
  const [selecionados, setSelecionados] = useState([]);
  const [loteVisivel, setLoteVisivel] = useState(false);
  const [formLote, setFormLote] = useState(normalizarTributos(null));
  const [manterNcmCest, setManterNcmCest] = useState(true);
  const [observacaoLote, setObservacaoLote] = useState('');
  const [salvandoLote, setSalvandoLote] = useState(false);
  const [errosLote, setErrosLote] = useState([]);
  const modoSelecao = status === 'PENDENTE';

  const ncmsDistintos = itens => [...new Set(itens.map(i => soDigitos(i.ncm)).filter(Boolean))];
  const ncmsSelecionados = ncmsDistintos(selecionados);
  const ncmDiferente = ncmsSelecionados.length > 1;
  const listaNcms = ncmsSelecionados.map(formatarNcm).join(', ');

  const alterarSelecao = novos => {
    const lista2 = novos || [];
    const antes = ncmsDistintos(selecionados).length;
    const depois = ncmsDistintos(lista2);
    if (depois.length > 1 && depois.length > antes) {
      toast.warn(`Atenção: produtos selecionados com NCM diferente (${depois.map(formatarNcm).join(', ')}).`, { toastId: 'ncm-diferente' });
    }
    setSelecionados(lista2);
  };

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    try {
      const params = {
        status,
        codigoEan: codigoEan || undefined,
        descricao: descricao || undefined,
        dataInicial: periodo && periodo[0] ? dateToIso(periodo[0]) : undefined,
        dataFinal: periodo && (periodo[1] || periodo[0]) ? dateToIso(periodo[1] || periodo[0]) : undefined,
      };
      const dados = await pesquisarRecebimentos(params);

      if (status === 'PENDENTE') {
        const ids = new Set(dados.map(d => d.id));
        if (idsConhecidos.current) {
          const novos = dados.filter(d => !idsConhecidos.current.has(d.id));
          if (novos.length) toast.info(`${novos.length} novo(s) produto(s) recebido(s) para conferência.`);
        }
        idsConhecidos.current = ids;
      }

      setLista(dados);
    } catch (e) {
      if (!silencioso) toast.error(mensagemErro(e, 'Erro ao consultar recebimentos.'));
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }, [status, codigoEan, descricao, periodo]);

  useEffect(() => {
    idsConhecidos.current = null;
    setSelecionados([]);
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (status !== 'PENDENTE') return undefined;
    const timer = setInterval(() => carregar(true), INTERVALO_MS);
    return () => clearInterval(timer);
  }, [status, carregar]);

  const pesquisar = e => {
    e.preventDefault();
    carregar();
  };

  const reabrir = async r => {
    try {
      await reabrirRecebimento(r.id);
      toast.success('Recebimento reaberto para conferência.');
      onAlteracao();
      carregar();
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };

  const abrirLote = async () => {
    if (!selecionados.length) return;
    const ncm = valorComum(selecionados, 'ncm');
    const cest = valorComum(selecionados, 'cest');
    let base = normalizarTributos(null);
    // Mesmo NCM em todos: pré-preenche com a tributação mais frequente desse NCM na base
    if (ncm.length === 8) {
      try {
        const dados = await resumoNcm(ncm);
        if (dados && dados.modelo) base = normalizarTributos(dados.modelo);
      } catch (e) {
        // sem sugestão: segue com o formulário vazio
      }
    }
    setFormLote({
      ...base, id: null, codigoEan: '', descricao: '', cnpjCliente: '', registroProduto: null, ncm, cest: cest || base.cest || '',
    });
    setManterNcmCest(true);
    setObservacaoLote('');
    setErrosLote([]);
    setLoteVisivel(true);
  };

  const aplicarLote = async () => {
    let { erros } = validarRegrasTributarias(formLote);
    if (manterNcmCest) {
      erros = erros.filter(e => e.campo !== 'cest');
    } else if (soDigitos(formLote.ncm).length !== 8) {
      toast.warn('Informe o NCM (8 dígitos) ou marque para manter o NCM/CEST de cada produto.');
      return;
    }
    if (erros.length) {
      toast.error(`Corrija a tributação antes de conferir: ${resumoErros(erros)}`);
      return;
    }
    setSalvandoLote(true);
    try {
      const r = await conferirLoteRecebimentos({
        ids: selecionados.map(s => s.id),
        tributos: formLote,
        manterNcmCest,
        observacao: observacaoLote || undefined,
      });
      const ok = (r.conferidos || 0) + (r.jaBaixados || 0);
      if (ok) toast.success(`${ok} recebimento(s) conferido(s).`);
      onAlteracao();
      await carregar();
      if (r.erros && r.erros.length) {
        // Mantém selecionados somente os que falharam, para corrigir e aplicar de novo
        const idsErro = new Set(r.erros.map(e => e.id));
        setSelecionados(atual => atual.filter(s => idsErro.has(s.id)));
        setErrosLote(r.erros);
        toast.warn(`${r.erros.length} produto(s) não foram conferidos. Veja os motivos na janela.`);
      } else {
        setSelecionados([]);
        setLoteVisivel(false);
      }
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao conferir os selecionados.'));
    } finally {
      setSalvandoLote(false);
    }
  };

  const colunaProduto = r => (
    <div className="tb-cell-prod">
      <strong>{r.descricao}</strong>
      <span className="tb-mono">{r.codigoEan || `Sem EAN · Registro ${r.registroProduto || '—'}`}</span>
    </div>
  );

  const colunaNota = r => (
    <div className="tb-cell-sub">
      <span>{r.numeroNotaFiscal ? `NF ${r.numeroNotaFiscal}` : '—'}</span>
      <small>{r.nomeFornecedor || formatarCnpj(r.cnpjFornecedor)}</small>
    </div>
  );

  const colunaFiscalNota = r => (
    <div className="tb-cell-sub tb-mono">
      <span>{r.ncm ? formatarNcm(r.ncm) : '—'}</span>
      <small>{[r.cest && `CEST ${r.cest}`, r.cfop && `CFOP ${r.cfop}`, r.cst && `CST ${r.cst}`].filter(Boolean).join(' · ')}</small>
    </div>
  );

  const colunaOrigem = r => (
    <div className="tb-cell-sub">
      <span>{r.nomeEmpresa || '—'}</span>
      <small>{formatarCnpj(r.cnpjEmpresa)}</small>
    </div>
  );

  const colunaData = r => (
    <div className="tb-cell-sub">
      <span>{r.dataHoraUltimoEnvio || r.dataHoraRecebimento}</span>
      <small>{r.quantidadeEnvios > 1 ? `${r.quantidadeEnvios} envios` : ''}</small>
    </div>
  );

  const colunaAcoes = r => (
    <div className="tb-row-actions">
      {r.status === 'PENDENTE' ? (
        <button type="button" className="tb-btn tb-btn-primary tb-btn-sm" onClick={() => navigate(`/tributos/conferencia/${r.id}`)}>
          <i className="pi pi-pencil" /> Conferir
        </button>
      ) : (
        <>
          <button type="button" className="tb-btn tb-btn-ghost tb-btn-sm" onClick={() => navigate(`/tributos/conferencia/${r.id}`)}>
            <i className="pi pi-eye" /> Ver
          </button>
          {r.status !== 'ATUALIZADO' ? (
            <button type="button" className="tb-btn tb-btn-ghost tb-btn-sm" title="Reabrir para nova conferência" onClick={() => reabrir(r)}>
              <i className="pi pi-replay" />
            </button>
          ) : null}
        </>
      )}
    </div>
  );

  return (
    <div className="tb-page">
      <div className="tb-page-head">
        <div>
          <h1>Recebimentos</h1>
          <p>Produtos enviados pela Retaguarda na entrada de notas fiscais.</p>
        </div>
        <button type="button" className="tb-btn tb-btn-ghost" onClick={() => carregar()}>
          <i className={`pi pi-refresh ${carregando ? 'pi-spin' : ''}`} /> Atualizar
        </button>
      </div>

      <section className="tb-card">
        <form className="tb-filters" onSubmit={pesquisar}>
          <SelectButton value={status} options={OPCOES_STATUS} onChange={e => e.value && setStatus(e.value)} className="tb-segmented" />
          <div className="tb-filters-row">
            <span className="p-input-icon-left">
              <i className="pi pi-qrcode" />
              <InputText value={codigoEan} onChange={e => setCodigoEan(e.target.value)} placeholder="EAN" />
            </span>
            <span className="p-input-icon-left tb-grow">
              <i className="pi pi-search" />
              <InputText value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descrição" />
            </span>
            {status !== 'PENDENTE' ? (
              <Calendar
                value={periodo}
                onChange={e => setPeriodo(e.value)}
                selectionMode="range"
                dateFormat="dd/mm/yy"
                readOnlyInput
                showIcon
                placeholder="Período"
              />
            ) : null}
            <button type="submit" className="tb-btn tb-btn-primary"><i className="pi pi-filter" /> Filtrar</button>
          </div>
        </form>

        {modoSelecao && selecionados.length ? (
          <div className="tb-lote-barra">
            <span>
              <strong>{selecionados.length}</strong> produto(s) selecionado(s)
              {ncmDiferente ? (
                <span className="tb-tag tb-tag-warn tb-lote-alerta" title={listaNcms}>
                  <i className="pi pi-exclamation-triangle" /> {ncmsSelecionados.length} NCMs diferentes
                </span>
              ) : null}
            </span>
            <div>
              <button type="button" className="tb-btn tb-btn-ghost tb-btn-sm" onClick={() => setSelecionados([])}>
                <i className="pi pi-times" /> Limpar seleção
              </button>
              <button type="button" className="tb-btn tb-btn-primary tb-btn-sm" onClick={abrirLote}>
                <i className="pi pi-check-square" /> Conferir selecionados
              </button>
            </div>
          </div>
        ) : null}

        <DataTable
          value={lista}
          selectionMode={modoSelecao ? 'checkbox' : null}
          selection={modoSelecao ? selecionados : null}
          onSelectionChange={e => alterarSelecao(e.value)}
          showSelectAll={false}
          sortField="descricao"
          sortOrder={1}
          loading={carregando}
          paginator
          rows={20}
          rowsPerPageOptions={[20, 50, 100]}
          dataKey="id"
          responsiveLayout="stack"
          breakpoint="900px"
          emptyMessage={status === 'PENDENTE' ? 'Nenhum produto aguardando conferência.' : 'Nenhum recebimento encontrado.'}
          className="tb-table"
          onRowDoubleClick={e => navigate(`/tributos/conferencia/${e.data.id}`)}
        >
          {modoSelecao ? <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} style={{ width: '3rem' }} /> : null}
          <Column header="Produto" field="descricao" sortable body={colunaProduto} />
          <Column header="Dados da NF" body={colunaFiscalNota} style={{ width: '13rem' }} />
          <Column header="Nota / Fornecedor" body={colunaNota} style={{ width: '14rem' }} />
          <Column header="Cliente" body={colunaOrigem} style={{ width: '13rem' }} />
          <Column header="Recebido em" body={colunaData} style={{ width: '11rem' }} />
          <Column header="Situação" body={r => <StatusTag status={r.status} />} style={{ width: '9rem' }} />
          <Column body={colunaAcoes} style={{ width: '9rem' }} />
        </DataTable>
      </section>

      <Dialog
        className="tb-dialog"
        header={`Conferir ${selecionados.length} produto(s) selecionado(s)`}
        visible={loteVisivel}
        onHide={() => !salvandoLote && setLoteVisivel(false)}
        style={{ width: '70rem', maxWidth: '96vw' }}
        maximizable
        footer={(
          <div className="tb-lote-footer">
            <span className="tb-lote-opcao">
              <Checkbox inputId="manterNcmCest" checked={manterNcmCest} onChange={e => setManterNcmCest(e.checked)} />
              <label htmlFor="manterNcmCest">Manter o NCM e o CEST de cada produto</label>
            </span>
            <div>
              <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setLoteVisivel(false)} disabled={salvandoLote}>Cancelar</button>
              <button type="button" className="tb-btn tb-btn-primary" onClick={aplicarLote} disabled={salvandoLote || !selecionados.length}>
                {salvandoLote ? <i className="pi pi-spin pi-spinner" /> : <i className="pi pi-check" />} Conferir {selecionados.length} produto(s)
              </button>
            </div>
          </div>
        )}
      >
        {errosLote.length ? (
          <section className="tb-regras tb-regras-erro">
            <header><i className="pi pi-times-circle" /> {errosLote.length} produto(s) não conferido(s) — continuam selecionados</header>
            <ul>
              {errosLote.map(e => <li key={e.id} className="tb-regra-erro"><strong>{e.descricao}</strong>: {e.mensagem}</li>)}
            </ul>
          </section>
        ) : null}
        {ncmDiferente ? (
          <section className="tb-regras tb-regras-aviso">
            <header><i className="pi pi-exclamation-triangle" /> Produtos com NCM diferente</header>
            <ul>
              <li className="tb-regra-aviso">
                Os produtos selecionados têm {ncmsSelecionados.length} NCMs: {listaNcms}. Confira se a mesma tributação vale para todos.
              </li>
              {!manterNcmCest ? (
                <li className="tb-regra-aviso">Com a opção desmarcada, todos passarão a ter o NCM/CEST informado abaixo.</li>
              ) : null}
            </ul>
          </section>
        ) : null}
        <p className="tb-obs">
          A tributação abaixo será gravada em todos os produtos selecionados, que serão marcados como conferidos.
          Cada produto mantém a sua descrição{manterNcmCest ? ', NCM e CEST' : ''}.
        </p>
        <FormTributos valor={formLote} onChange={setFormLote} ocultarIdentificacao ncmCestDoProduto={manterNcmCest} />
        <div className="tb-field" style={{ marginTop: 12 }}>
          <label htmlFor="obsLote">Observação (opcional)</label>
          <InputText id="obsLote" value={observacaoLote} onChange={e => setObservacaoLote(e.target.value)} maxLength={500} />
        </div>
      </Dialog>
    </div>
  );
}

Recebimentos.propTypes = {
  onAlteracao: PropTypes.func.isRequired,
};
